import React, { useCallback, useEffect, useRef, useState } from "react";
import { Image } from "@wss/image";
import debounce from "lodash/debounce";
import Link from "./Link";
import VideoIcon from "./VideoIcon";
import Content from "./Content";
import Heading from "./Heading";
import Resource from "../Resource";
import useResizeObserver from "~/ProductDetails/VirtualGrouping/Hooks/useResizeObserver";
import { calculateTitleLineCounts } from "../utilities";
import Description from "./Description";

interface CardGridProps {
  resources: Resource[];
  sliceCount?: number;
  startSliceIndex?: number;
  hasRecentPosts?: boolean;
}

const CardGrid = ({
  resources,
  sliceCount,
  startSliceIndex,
  hasRecentPosts = false,
}: CardGridProps) => {
  const titleRefs = useRef<(HTMLHeadingElement | null)[]>([]);
  const descriptionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [titleLineCounts, setTitleLineCounts] = useState<boolean[]>([]);
  const [isOverflowing, setIsOverflowing] = useState<boolean[]>([]);
  const { width } = useResizeObserver();
  const isDesktopWidth = width >= 768;

  const updateTitleLines = useCallback(() => {
    const lineCounts = calculateTitleLineCounts(titleRefs);
    setTitleLineCounts(lineCounts);
    setIsLoading(false);
  }, [titleRefs]);

  const checkOverflow = useCallback(
    debounce(() => {
      requestAnimationFrame(() => {
        if (descriptionRefs.current.length > 0) {
          const overflowStatus = descriptionRefs.current.map((descRef) => {
            if (descRef) {
              return descRef.scrollHeight > descRef.clientHeight;
            }
            return false;
          });
          setIsOverflowing(overflowStatus);
        }
      });
    }, 100),
    [],
  );

  useEffect(() => {
    const handleResize = () => {
      updateTitleLines();
      checkOverflow();
    };
    handleResize();
  }, [width, updateTitleLines, checkOverflow, isDesktopWidth]);

  const displayedResources = (() => {
    if (startSliceIndex !== undefined && sliceCount !== undefined) {
      return resources.slice(startSliceIndex, startSliceIndex + sliceCount);
    }
    if (startSliceIndex !== undefined) {
      return resources.slice(startSliceIndex);
    }
    if (sliceCount !== undefined) {
      return resources.slice(0, sliceCount);
    }
    return resources;
  })();

  return (
    <div
      className={`card-grid-container grid grid-cols-1 gap-y-3 mb-10 md:mt-4 md:gap-6 md:grid-cols-2 xxl:grid-cols-3 ${
        displayedResources.length > 3 ? "xxl:grid-rows-2" : ""
      }`}
    >
      {displayedResources.map((singleResource, index) => {
        const isFirstRecentPostItem =
          hasRecentPosts && startSliceIndex === 0 && index === 0;
        return (
          <Link
            key={singleResource.title}
            href={singleResource.link}
            className={`group feature-link border-t border-b-0 border-x-0 border-gray-200 border-solid flex hover:border-gray-300 md:border md:flex-col relative w-full md:rounded md:shadow-sm ${
              isFirstRecentPostItem
                ? "flex-col md:col-span-2 xxl:col-span-1 xxl:row-span-2"
                : ""
            }`}
          >
            <div className="flex-shrink-0 relative">
              {singleResource.showVideoIcon && (
                <div className="video-icon-container absolute bg-black/75 fill-white flex items-center justify-center px-2 py-1 top-5 right-2 rounded z-10 md:top-2">
                  <VideoIcon
                    iconProps={{
                      className:
                        "flex-shrink-0 h-[13px] w-[13px] md:h-5 md:w-6",
                    }}
                  />
                </div>
              )}
              <Image
                alt={singleResource.title}
                className={`feature-img flex-shrink-0 h-auto mt-4 rounded object-cover md:w-full md:rounded-b-none md:mt-0 md:rounded-t ${
                  isFirstRecentPostItem ? "w-full" : "w-36"
                }`}
                src={
                  singleResource.imageThumbnail ||
                  "//cdnimg.webstaurantstore.com/uploads/images/2015/6/fallback-article.png"
                }
                height={47}
                width={120}
              />
              <div className="absolute bg-black inset-0 opacity-0 transition-opacity group-hover:opacity-[0.12]" />
            </div>
            <div
              className={`flex flex-col md:border-b-0 md:border-gray-200 md:border-solid md:border-t md:border-x-0 md:gap-y-0 md:px-5 md:py-4 ${
                isFirstRecentPostItem
                  ? "justify-start pt-4 px-5 py-4 h-[106px] md:h-full md:max-h-80 xxl:max-h-[335px] xxxl:max-h-[360px] xxxxl:max-h-[423px]"
                  : "justify-center pl-3 pt-3"
              }`}
            >
              <div className="feature-category-date-container flex items-center justify-start mb-1.5 md:mb-2">
                <Content className="feature-category-name font-semibold leading-3 mb-0 text-gray-600 text-xs">
                  {singleResource.universalCategoryName}
                </Content>
                {singleResource.universalCategoryName &&
                singleResource.dateModified ? (
                  <span
                    className={`leading-3 mx-1 text-gray-350 md:inline-flex ${
                      isFirstRecentPostItem ? "inline-flex" : "hidden"
                    }`}
                  >
                    &#8226;
                  </span>
                ) : null}
                <Content
                  contentType="date"
                  className={`feature-date leading-3 mb-0 text-gray-600 text-xs md:inline-flex ${
                    isFirstRecentPostItem ? "" : "hidden md:mt-0"
                  }`}
                  dateModified={singleResource.dateModified}
                />
              </div>
              <div
                className="feature-text-container overflow-hidden relative"
                ref={(descElement) => {
                  descriptionRefs.current[index] = descElement;
                }}
              >
                <Heading
                  headingType="resourceTitle"
                  className="feature-title-text font-bold leading-[14px] line-clamp-2 mb-1.5 md:leading-4 md:text-base text-gray-800 text-sm"
                  ref={(titleElement) => {
                    titleRefs.current[index] = titleElement;
                  }}
                >
                  {singleResource.title}
                </Heading>
                {!isLoading && (
                  <Description
                    isFirstRecentPostItem={isFirstRecentPostItem}
                    isDesktopWidth={isDesktopWidth}
                    isTwoLines={titleLineCounts[index] ?? false}
                    resource={singleResource}
                  />
                )}
                {isOverflowing[index] && (
                  <div
                    className={`${
                      isFirstRecentPostItem
                        ? "absolute bottom-0 left-0 w-full h-5 md:h-9 bg-gradient-to-t from-[rgba(255,255,255,0.9)] to-[rgba(255,255,255,0.5)] pointer-events-none"
                        : ""
                    }`}
                  />
                )}
              </div>
              <Content
                contentType="date"
                className={`feature-date leading-3 inline-block mb-0 text-gray-600 text-xs md:hidden ${
                  isFirstRecentPostItem ? "hidden" : "md:mt-0"
                }`}
                dateModified={singleResource.dateModified}
              />
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default CardGrid;
